/**
 * Quality preset auto-detection.
 *
 * On first frame we read the GPU vendor/renderer string and benchmark a
 * trivial render to pick a level. Levels gate NPC count, shadow res,
 * post-processing, and terrain LOD aggression.
 */

export type QualityLevel = 'low' | 'medium' | 'high';

export interface QualityProfile {
  level: QualityLevel;
  npcsPerStage: number;
  treesPerHectare: number;
  shadowMapSize: number;
  bloom: boolean;
  ssao: boolean;
  pixelRatio: number;
  drawDistance: number;
}

export const PROFILES: Record<QualityLevel, QualityProfile> = {
  low: {
    level: 'low',
    npcsPerStage: 80,
    treesPerHectare: 8,
    shadowMapSize: 1024,
    bloom: false,
    ssao: false,
    pixelRatio: 1,
    drawDistance: 700
  },
  medium: {
    level: 'medium',
    npcsPerStage: 200,
    treesPerHectare: 16,
    shadowMapSize: 2048,
    bloom: true,
    ssao: false,
    pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
    drawDistance: 1100
  },
  high: {
    level: 'high',
    npcsPerStage: 400,
    treesPerHectare: 32,
    shadowMapSize: 2048,
    bloom: true,
    ssao: true,
    pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
    drawDistance: 1500
  }
};

/** Best-effort GPU class detection from the renderer string. */
export function autoDetectQuality(gl: WebGL2RenderingContext): QualityProfile {
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string : '';
  const r = (renderer || '').toLowerCase();

  if (
    r.includes('apple m') ||
    r.includes('rtx') ||
    r.includes('rx 7') ||
    r.includes('rx 6') ||
    r.includes('arc a')
  ) {
    return PROFILES.high;
  }
  if (
    r.includes('intel iris') ||
    r.includes('uhd graphics') ||
    r.includes('apple gpu') ||
    r.includes('mali') ||
    r.includes('adreno')
  ) {
    return PROFILES.medium;
  }
  if (r.includes('software') || r.includes('llvmpipe') || r.includes('swiftshader')) {
    return PROFILES.low;
  }
  return PROFILES.medium;
}
